import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = process.env.REACT_APP_API_URL;

export const fetchStones = async (requireAuth = false) => {
  try {
    let url = `${API_URL}/api/stones/public`;
    let headers = {
      'Content-Type': 'application/json'
    };
    
    if (requireAuth) {
      // Get auth token for authenticated requests
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (token) {
        url = `${API_URL}/api/stones`;
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    return data.stones || [];
    
  } catch (error) {
    console.error('Error fetching stones:', error);
    throw error;
  }
};

export const submitAccessRequest = async ({ name, email, relationship, message }) => {
  try {
    const response = await fetch(`${API_URL}/api/access-request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ name, email, relationship, message })
    });
    
    if (!response.ok) {
      throw new Error('Failed to submit access request');
    }
    
    return await response.json();
    
  } catch (error) {
    console.error('Error submitting access request:', error);
    throw error;
  }
};

export const fetchStoneDetails = async (stoneId) => {
  try {
    let url = `${API_URL}/api/stones/${stoneId}`;
    let headers = {
      'Content-Type': 'application/json'
    };
    
    // Try to get auth token if user is logged in
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (authError) {
      // User not logged in, that's okay for public stones
      console.log('No auth token, viewing as public');
    }
    
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      if (response.status === 403) {
        const data = await response.json();
        throw new Error(data.error);
      }
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
    
  } catch (error) {
    console.error('Error fetching stone details:', error);
    throw error;
  }
};

export const submitVisit = async (visitData) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    
    if (!token) {
      throw new Error('Must be logged in to submit a visit');
    }
    
    const response = await fetch(`${API_URL}/api/visits`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(visitData)
    });
    
    if (!response.ok) {
      const text = await response.text().catch(()=> '');
      throw new Error(`Failed to submit visit: ${response.status} ${text}`);
    }
    
    return await response.json();
    
  } catch (error) {
    console.error('Error submitting visit:', error);
    throw error;
  }
};

export const getUploadUrl = async ({ fileName, fileType, visitId }) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    
    if (!token) {
      throw new Error('Must be logged in to upload files');
    }
    
    const response = await fetch(`${API_URL}/api/upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ fileName, fileType, visitId })
    });
    
    if (!response.ok) {
      throw new Error('Failed to get upload URL');
    }
    
    return await response.json();
    
  } catch (error) {
    console.error('Error getting upload URL:', error);
    throw error;
  }
};

export const completeVisitUpload = async ({ visitId, mediaFiles }) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    
    if (!token) {
      throw new Error('Must be logged in to complete upload');
    }
    
    const response = await fetch(`${API_URL}/api/visits/complete-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ visitId, mediaFiles })
    });
    
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Failed to complete upload: ${response.status} ${text}`);
    }
    
    return await response.json();
    
  } catch (error) {
    console.error('Error completing visit upload:', error);
    throw error;
  }
};